import React, { useState, useEffect, useMemo } from 'react';
import { Button, Input, Form, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { CampaignItemDto } from '@api/Api';
import { useAuth } from '@hooks/useAuth';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { removeExtraSpaces } from '@helpers/stringTransformers';
import { createNewCampaign } from '@redux/actions/campaignAction';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { toggleReachedEntityLimitModal } from '@redux/actions/modalAction';
import { DiamondUpgradeIcon, EyePrivateIcon, EyePublicIcon } from '@icons';

type CampaignCreationStepProps = {
  onCloseModal: (campaignId?: string) => void;
  setStep: (value: 'campaign' | 'invite') => void;
  setCampaign: (campaign: CampaignItemDto) => void;
};

export default function CampaignCreationStep({
  onCloseModal,
  setStep,
  setCampaign
}: CampaignCreationStepProps) {
  const dispatch = useDispatch();
  const { onStartTour, setGoToStep } = useOnboardingProcess();
  const { user } = useAuth(true, 'internal');
  const [form] = Form.useForm();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [canCreateCampaign, setCanCreateCampaign] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [canCreatePrivate, setCanCreatePrivate] = useState<boolean>(false);
  const { currentOrganization } = useOrganization();
  const [nameValueLength, setNameValueLength] = useState<number>(0);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValueLength = e.target.value.length;
    setNameValueLength(newValueLength);
  };

  useEffect(() => {
    if (!isPrivate && currentWorkspace?.permissions.createCampaigns)
      setCanCreateCampaign(true);
    if (isPrivate && currentWorkspace?.permissions.createPrivateCampaigns)
      setCanCreateCampaign(true);
    setCanCreatePrivate(
      !!currentOrganization.entity?.features.privateCampaigns &&
        !!currentWorkspace?.permissions.createPrivateCampaigns
    );
  }, [currentWorkspace, isPrivate]);

  const isUpgradeCanVisible = useMemo(() => {
    return !currentOrganization.entity?.features.privateCampaigns;
  }, [currentOrganization.entity?.features.privateCampaigns]);

  const onFinish = async ({ name }: { name: string }) => {
    if (isSubmitting) return;
    const newName = removeExtraSpaces(name);
    try {
      setIsSubmitting(true);
      const { campaign } = await createNewCampaign({
        workspaceId: currentWorkspace?.id || '',
        isPrivate,
        name: newName,
        forOnboarding: true
      })(dispatch);
      MixpanelService.track(user.id, MixpanelEventType.MANAGE_CAMPAIGNS, {
        campaignName: newName,
        deleteCampaign: false,
        newCampaign: true,
        privateCampaign: isPrivate,
        sharedCampaign: !isPrivate,
        addTaskGroup: false,
        addContributor: false
      });
      if (!isPrivate) {
        onCloseModal(campaign.id);
        return;
      }
      setCampaign(campaign);
      setStep('invite');
      setTimeout(() => {
        setGoToStep(1);
      }, 100);
    } catch (e) {
      setIsSubmitting(false);
      dispatch(
        toggleReachedEntityLimitModal({ visible: true, entity: 'campaign' })
      );
    }
  };

  return (
    <div className="modal_container enitity-creation-modal campaign-folder-creation-modal-container tour-create-campaign-modal">
      <Form form={form} onFinish={onFinish} validateTrigger="onChange">
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 32 }}
        >
          New project
        </h2>
        <Form.Item
          className="enitity-creation-modal__name-item enitity-creation-modal__name-item--counter"
          name="name"
          rules={[
            {
              whitespace: true,
              message: "'name' cannot be empty."
            },
            {
              max: 60,
              message: `${nameValueLength}/60 characters. Name has exceeded character limit.`
            },
            {
              pattern: /^[a-zA-Z 0-9 (".,:'\-—/!?#+=*@&©)]*$/,
              message: `Please use only letters, numbers or the following special symbols .,:()'-—/!?#+=*@&`
            },
            {
              required: true,
              message: 'Name is required.'
            }
          ]}
          style={{ margin: '32px 0px 24px', height: 78 }}
        >
          <Input
            onChange={onNameChange}
            placeholder="Add project name"
            size="large"
            showCount={{
              formatter: ({ count }) => (
                <span className="main-body-text main-body-text--semibold main-body-text--small main-body-text--dark-gray">
                  {count}/60 characters
                </span>
              )
            }}
            maxLength={60}
          />
        </Form.Item>
        <div className="modal_container__campaign">
          <p className="copy">
            {' '}
            <span>project</span> access
          </p>
          <div className="types">
            <button
              type="button"
              className={`type_block type_block--public ${
                !isPrivate ? 'type_block_selected' : ''
              }`}
              onClick={() => setIsPrivate(false)}
            >
              <div className="title">
                <EyePublicIcon />
                Shared project
              </div>
              <div className="description">
                Anyone in this workspace can access shared projects
              </div>
            </button>
            <UpgradePlanTooltip
              text="Upgrade to a Team plan to access this feature."
              isCanVisible={isUpgradeCanVisible}
              innerStyle={{
                transform: 'translate(-20px, 25px)'
              }}
              isShowUpgradeText={false}
            >
              <div>
                <button
                  type="button"
                  className={`type_block type_block--private ${
                    isPrivate ? 'type_block_selected' : ''
                  } ${!canCreatePrivate ? 'disabled' : ''}`}
                  onClick={() => {
                    if (!currentOrganization.subscription) return;
                    if (!canCreatePrivate) return;
                    setIsPrivate(true);
                  }}
                >
                  <div className="title">
                    <EyePrivateIcon /> Private project{' '}
                    {isUpgradeCanVisible && (
                      <DiamondUpgradeIcon className="upgrade-icon" />
                    )}
                  </div>
                  <div className="description">
                    Only invited members and contributors can access private
                    projects
                  </div>
                </button>
              </div>
            </UpgradePlanTooltip>
          </div>
        </div>
        <div className="modal_container__actions">
          {!canCreateCampaign && (
            <Button
              type="text"
              onClick={() => {
                onStartTour({ type: undefined });
                onCloseModal();
              }}
              style={{
                marginRight: 12,
                border: 'none',
                height: 48,
                padding: '12px 19px'
              }}
              htmlType="button"
            >
              Cancel
            </Button>
          )}
          <Tooltip
            overlayClassName="long-text-hint"
            overlayInnerStyle={{ maxWidth: 285 }}
            title={
              !canCreateCampaign &&
              'Your current role in StreamWork doesn’t allow you to create a project.'
            }
            trigger="hover"
            placement="bottom"
          >
            <Button
              type="primary"
              disabled={!canCreateCampaign || !nameValueLength}
              loading={isSubmitting}
              size="large"
              htmlType="submit"
              style={{ width: 152, height: 48, padding: '8px 12px' }}
            >
              {!isPrivate && 'Create'}
              {isPrivate && 'Next'}
            </Button>
          </Tooltip>
        </div>
      </Form>
    </div>
  );
}
